import { add, type Duration, differenceInDays, format, isAfter, isMatch, lightFormat, parseISO } from 'date-fns';
import { nb } from 'date-fns/locale';

/**
 * @example '24.12.2024'
 */
type DisplayDate = `${number}.${number}.${number}`;
/**
 * @example '2024-12-24'
 */
type ShortDate = `${number}-${number}-${number}`;

const formats = {
  displayDate: 'dd.MM.yyyy',
  shortDate: 'yyyy-MM-dd',
  shortDateFirstOfMonth: 'yyyy-MM-01',
  time: 'HH.mm',
} as const;

const yesterday = () => add(new Date(), { days: -1 });

export const changeDate = (date: Date, args: Duration): Date => add(date, args);

export const formatDate = (date: Date, format: keyof typeof formats) => {
  return lightFormat(date, formats[format]);
};

export const getCookieLoginDate = () => lightFormat(new Date(), `${formats.displayDate} ${formats.time}`);

/**
 * Format date as `dd.MM.yyyy` format
 */
export const utcToDisplayDate = (date?: string | Date): DisplayDate | '' => {
  if (!date) return '';
  const _date = typeof date === 'string' ? parseISO(date) : date;
  return lightFormat(_date, formats.displayDate) as DisplayDate;
};

/**
 * Format date as `dd.MM.yyyy, HH:mm` format
 */
export const utcToDisplayDateWithTime = (date?: string | Date): string => {
  if (!date) return '';
  const _date = typeof date === 'string' ? parseISO(date) : date;
  return lightFormat(_date, `${formats.displayDate}, ${formats.time}`);
};

export const displayDateToShortDate = (date: DisplayDate): ShortDate =>
  date.split('.').reverse().join('-') as ShortDate;

export const isAfterYesterday = (date: ShortDate | DisplayDate | string): boolean => {
  if (!date) {
    return false;
  }
  const isDisplayDate = isMatch(date, formats.displayDate);
  const _date = isDisplayDate ? displayDateToShortDate(date as DisplayDate) : date;
  return isAfter(parseISO(_date), yesterday());
};

export const isOverdue = (isoDate?: string): boolean => {
  if (!isoDate) return false;
  return isAfter(new Date(), parseISO(isoDate));
};

export const dateFromDisplayDate = (date: DisplayDate): Date => parseISO(displayDateToShortDate(date));

/**
 * Format a `dd.MM.yyyy` date the long way round, in Norwegian.
 *
 * @example '14. mars 2024'
 */
export const displayDateToLongDate = (date?: string): string => {
  if (!date || !isMatch(date, formats.displayDate)) return '';
  return format(dateFromDisplayDate(date as DisplayDate), 'd. MMMM yyyy', { locale: nb });
};

export const diffInDays = differenceInDays;
